<script setup lang="ts">
import type { IGetAlbumFilters } from "@/api"
import type { IGalleryFilters, MediaTypeFilter } from "@/components/GalleryFiltersDrawer/galleryFilters.interface"
import type { Comment, Media } from "@/models"
import { useInfiniteQuery, useQueryClient } from "@tanstack/vue-query"
import { useIntersectionObserver, useLocalStorage, useMediaQuery, useUrlSearchParams } from "@vueuse/core"
import { computed, ref, useTemplateRef, watchEffect } from "vue"
import { listMedia, useGetAlbum, useGetEventVault, usePinMedia } from "@/api"
import { CommentsDrawer, FlagContentModal, GalleryActions, GalleryFeed, GalleryFiltersDrawer, GalleryGrid, GuestEmptyStatePanel, Lightbox } from "@/components"
import { emitSelectedAlbum } from "@/components/Nav/AlbumIdEventBus"
import { Banner, Button } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useFilters, useGalleryPermissions, useLightbox, useModal, useTranslation } from "@/composables"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { CommentsDrawerKey, FlagContentModalKey, GalleryFiltersDrawerKey } from "@/symbols"
import { GalleryMode } from "@/types"

const props = defineProps<{
  eventVaultId: number
  eventAlbumId?: number
}>()

const emit = defineEmits<{
  (e: "upload"): void
}>()

const eventVaultStore = useEventVaultStore()

const toast = useToast()
const { t } = useTranslation()

const queryClient = useQueryClient()

const queryString = useUrlSearchParams<{ album?: unknown, upload?: unknown, mode?: unknown }>()

const { toggle: toggleFilters } = useModal(GalleryFiltersDrawerKey)
const { toggle: toggleCommentDrawer, isActive: isCommentDrawerActive } = useModal(CommentsDrawerKey)
const { toggle: toggleFlagContentModal } = useModal(FlagContentModalKey)

const albumId = computed(() => props.eventAlbumId)

const isMobileView = useMediaQuery("(max-width: 1024px)")
const galleryModePreference = useLocalStorage<GalleryMode | undefined>("gallery-mode", undefined)
const pendingApprovalBannerDismissed = useLocalStorage<boolean>("pending-approval-banner-dismissed", false)

function dismissPendingApprovalBanner() {
  pendingApprovalBannerDismissed.value = true
}

function getInitialMode(): GalleryMode {
  if (queryString.mode && typeof queryString.mode === "string") {
    return queryString.mode === "FEED" ? GalleryMode.feed : GalleryMode.grid
  }
  if (galleryModePreference.value) {
    return galleryModePreference.value
  }
  return isMobileView.value ? GalleryMode.feed : GalleryMode.grid
}

const mode = ref<GalleryMode>(getInitialMode())

const selectedMediaItem = ref<Media | undefined>()
const selectedComment = ref<Comment | undefined>()
const flaggedContent = ref<{ type: "media", content: Media } | { type: "comment", content: Comment } | undefined>()

const scrollEndRef = useTemplateRef<HTMLSpanElement | null>("scrollEndRef")
const isScrollEndVisible = ref(false)

// Set up intersection observer for infinite scroll
useIntersectionObserver(
  scrollEndRef,
  ([{ isIntersecting }]) => {
    isScrollEndVisible.value = isIntersecting
  },
  {
    root: null,
    rootMargin: "200px",
    threshold: 0.1,
  },
)

watchEffect(() => {
  if (queryString.album && typeof queryString.album === "string") {
    emitSelectedAlbum(Number(queryString.album))
  }
})

watchEffect(() => {
  if (!queryString.mode) {
    mode.value = getInitialMode()
  }
})

const mediaTypeDefaults: MediaTypeFilter[] = ["pics", "vids"]

const { filters, update, count: filterCount } = useFilters<IGalleryFilters>({
  perPage: 15,
  showGuestUploadsOnly: false,
  showPinnedUploadsOnly: false,
  mediaType: mediaTypeDefaults,
  tags: [],
})

const { filters: getAlbumFilters } = useFilters<IGetAlbumFilters>({
  include: ["settings"],
})

const { data: eventVault, isLoading: isLoadingEvent } = useGetEventVault(props.eventVaultId)
const { data: album, isLoading: isLoadingAlbum } = useGetAlbum(props.eventVaultId, albumId, getAlbumFilters)

const {
  data: media,
  fetchNextPage,
  isFetching,
  hasNextPage,
  isLoading: isLoadingMedia,
} = useInfiniteQuery({
  queryKey: ["listMedia", props.eventVaultId, albumId, filters],
  queryFn: pageParams =>
    listMedia(props.eventVaultId, albumId.value, {
      page: pageParams.pageParam ?? 1,
      perPage: filters.perPage,
      sortBy: filters.sortBy,
      orderBy: filters.orderBy,
      type: filters.mediaType?.length ? filters.mediaType.join(",") : undefined,
      showGuestUploadsOnly: filters.showGuestUploadsOnly,
      showPinnedUploadsOnly: filters.showPinnedUploadsOnly,
      tags: filters.tags,
      include: ["reactions", "tags"],
    }),
  getNextPageParam: (lastPage) => {
    if (lastPage.links?.Current === lastPage.links?.Last) {
      return undefined
    }

    const nextPage = Number(lastPage.links?.Next)

    return Number.isNaN(nextPage) ? undefined : nextPage
  },
  initialPageParam: 1,
})

const { mutate: pinMutation } = usePinMedia()

const isPinningMedia = ref<Media | undefined>()

function onPin(media?: Media) {
  if (!media || !props.eventVaultId) {
    return
  }

  pinMutation({ eventVaultId: props.eventVaultId, isPinned: !media.isPinned, media: [media] }, {
    async onSuccess() {
      isPinningMedia.value = media
      await queryClient.invalidateQueries({ queryKey: ["listMedia"] }).then(() => {
        isPinningMedia.value = undefined
        const content = media.isPinned ? t("gallery_page.remove_favorite_success") : t("gallery_page.add_favorite_success")
        toast.success({ title: t("success"), description: content, icon: "CircleCheck" })
      })
    },
  })
}

async function refreshMedia() {
  await queryClient.invalidateQueries({ queryKey: ["listMedia"] })
}

const mediaItems = computed<Media[]>(() => {
  return media.value?.pages.flatMap(p => p.media) ?? []
})

const { media: lightboxMedia, show } = useLightbox({ media: mediaItems })

watchEffect(() => (lightboxMedia.value = mediaItems.value))

function onView(index: number) {
  show(index + 1)
}

const canFetchMore = computed(() => !isLoadingMedia.value && mediaItems.value?.length && hasNextPage?.value)

function onFetchMore(): void {
  if (canFetchMore.value)
    fetchNextPage()
}

watchEffect(() => {
  if (canFetchMore.value && isScrollEndVisible.value && !isFetching.value)
    fetchNextPage()
})

const { isUploadsEnabled, isTaggingEnabled, isReactionsEnabled } = useGalleryPermissions(eventVault, album, {
  isHost: () => eventVaultStore.isHost ?? false,
})

function onModeChange(selectedMode: GalleryMode) {
  mode.value = selectedMode
  queryString.mode = selectedMode
  galleryModePreference.value = selectedMode
}

function onViewMediaComments(media?: Media) {
  selectedMediaItem.value = media
  toggleCommentDrawer()
}

function onFlagMedia(media?: Media) {
  if (media) {
    flaggedContent.value = { type: "media", content: media }
    selectedMediaItem.value = media
    toggleFlagContentModal()
  }
}

function onFlagComment(comment?: Comment) {
  if (comment) {
    flaggedContent.value = { type: "comment", content: comment }
    selectedComment.value = comment
    toggleFlagContentModal()
  }
}

function clearSelectedContent() {
  selectedComment.value = undefined
  selectedMediaItem.value = undefined
  flaggedContent.value = undefined
}

const isLoading = computed(() => {
  return Boolean(isLoadingEvent.value || isLoadingMedia.value || isLoadingAlbum.value)
})

const doesUserHavePendingApproval = computed(() => (media.value?.pages[0]?.meta?.totalAwaitingApproval ?? 0) > 0)

const pendingApprovalCount = computed(() => media.value?.pages[0]?.meta?.totalAwaitingApproval ?? 0)

const isEmptyGallery = computed(() => !mediaItems.value.length && !isLoadingMedia.value && !isFetching.value)

const totalMediaCount = computed(() => (eventVault.value?.totalPics ?? 0) + (eventVault.value?.totalVids ?? 0))

const showFilteredEmptyState = computed(() => Boolean(filterCount.value && totalMediaCount.value))
</script>

<template>
  <div data-test="event-guest-gallery">
    <div class="w-full pb-10">
      <GalleryActions
        :is-host="eventVaultStore.isHost"
        :event-vault="eventVault"
        :display-upload="isUploadsEnabled"
        :is-loading="isLoading"
        :is-loading-event="isLoadingEvent"
        :gallery-mode="mode"
        :pending-media-count="media?.pages[0]?.meta?.pendingMediaCount"
        :active-filters-count="filterCount"
        @upload="emit('upload')"
        @refresh="refreshMedia"
        @filter="toggleFilters"
        @change-mode="onModeChange"
      />

      <Banner
        v-if="doesUserHavePendingApproval && !pendingApprovalBannerDismissed"
        class="mb-4"
        :variant="eventVaultStore.isHost ? 'warning' : 'default'"
        :description="t(eventVaultStore.isHost ? 'gallery_page.pending_approval_host' : 'gallery_page.pending_approval_guest', { count: pendingApprovalCount })"
        :prefix-icon="eventVaultStore.isHost ? 'Clock' : 'CircleInfo'"
        :closable="!eventVaultStore.isHost"
        @close="dismissPendingApprovalBanner"
      />

      <div>
        <div v-if="eventVaultId && media">
          <transition name="gpx-fade" mode="out-in">
            <GuestEmptyStatePanel
              v-if="isEmptyGallery"
              key="gallery-empty"
              icon="Images"
              :include-actions-row="isUploadsEnabled || showFilteredEmptyState"
              data-test="gallery-empty-state"
            >
              <template #title>
                {{
                  showFilteredEmptyState
                    ? t("gallery_page.no_media_title_filters")
                    : eventVault?.eventVaultSettings?.videoEnabled
                      ? t("gallery_page.no_media_title")
                      : t("gallery_page.no_media_title_photos")
                }}
              </template>
              <template #description>
                {{
                  showFilteredEmptyState
                    ? t("gallery_page.no_media_description_filters")
                    : isUploadsEnabled
                      ? t("gallery_page.no_media_description_uploads")
                      : t("gallery_page.no_media_description_not_uploads")
                }}
              </template>
              <template #actions>
                <Button
                  v-if="isUploadsEnabled"
                  data-test="gallery-empty-upload-button"
                  class="w-full sm:w-auto"
                  prefix-icon="ImageUpload"
                  @click="emit('upload')"
                >
                  {{ eventVault?.eventVaultSettings?.videoEnabled ? t("upload_photos_and_videos") : t("upload_photos") }}
                </Button>
                <Button
                  v-if="showFilteredEmptyState"
                  data-test="gallery-empty-filters-button"
                  variant="secondary"
                  class="w-full sm:w-auto"
                  prefix-icon="Filter"
                  @click="toggleFilters"
                >
                  {{ t("gallery_page.edit_filters") }}
                </Button>
              </template>
            </GuestEmptyStatePanel>

            <GalleryFeed
              v-else-if="mode === GalleryMode.feed"
              data-test="gallery-feed"
              :event-settings="eventVault?.eventVaultSettings"
              :media="mediaItems"
              @view="onView"
              @view-comments="onViewMediaComments"
              @flag="onFlagMedia"
            />

            <GalleryGrid
              v-else
              data-test="gallery-grid"
              :items="mediaItems"
              :is-host="eventVaultStore.isHost"
              @view="onView"
            />
          </transition>
          <span v-if="mediaItems.length" ref="scrollEndRef" />
        </div>

        <div v-if="isLoadingMedia || isFetching" class="flex justify-center items-center" data-test="gallery-media-fetching">
          <img src="@/assets/gpx-loading.gif" alt="Loading Spinner">
        </div>
      </div>
    </div>

    <Lightbox
      :is-host="eventVaultStore.isHost"
      :total-media-count-display="(media?.pages[0]?.meta?.totalPhotos ?? 0) + (media?.pages[0]?.meta?.totalVideos ?? 0)"
      :is-fetching="isFetching"
      :is-pinning="isPinningMedia && isFetching"
      @favourite="onPin"
      @fetch-more="onFetchMore"
      @view-comments="onViewMediaComments"
      @flag="onFlagMedia"
    />

    <CommentsDrawer
      v-if="selectedMediaItem"
      :media="selectedMediaItem"
      :is-active="isCommentDrawerActive"
      @flag="onFlagComment"
    />

    <GalleryFiltersDrawer
      :event-vault-id="eventVaultId"
      :filters="filters"
      :tags-enabled="isTaggingEnabled"
      :reactions-enabled="isReactionsEnabled"
      @update:filters="update"
    />

    <FlagContentModal
      v-if="flaggedContent"
      :event-vault-id="eventVaultId"
      :content="flaggedContent"
      @cancel="clearSelectedContent"
      @success="clearSelectedContent"
      @error="clearSelectedContent"
    />
  </div>
</template>
